import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';

import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import { ResendLink } from './resend-link';
import { reformatPhoneNumber } from '../phone-number-helper/';
import { ErrorMessage } from './error-message';

import {
  STAMPS_SMS_RESEND_CODE,
  STAMPS_SMS_VERIFY_CODE_SUCCESS,
  STAMPS_SMS_VERIFY_CODE_FAILURE_CONTACT_STAMPS,
  STAMPS_SMS_VERIFY_CODE_WRONG_CODE,
} from '../analytics-event-strings';

import style from './style.module.less';
import sharedStyle from '../style.module.less';

const ONE_SECOND = 1000;

const unknown = 'unknown';
const invalidCodeResponse = 'Invalid verification code';
const tooManyAttemptsResponse = 'Too many attempts.';
const contactThirdPartyResponse = 'Contact Third Party';
const okMessagesWithFailedVerification = [tooManyAttemptsResponse, contactThirdPartyResponse];

export const VerificationChallenge = ({
  phoneNumber = '',
  className = '',
  goToPreviousStep,
  requestChallenge,
  trackEvent = noop,
  goToNextStep,
  requestChallengeCode,
  setVerification = noop,
}) => {
  const [challengeCode, setChallengeCode] = useState('');
  const [hasRequestedChallenge, setHasRequestedChallenge] = useState(false);
  const [hasRequestedCode, setHasRequestedCode] = useState(false);
  const [requestCodeSuccess, setRequestCodeSuccess] = useState();
  const [errorMessage, setErrorMessage] = useState();

  const isNextDisabled = challengeCode === '' || hasRequestedChallenge;
  const formattedNumber = reformatPhoneNumber(phoneNumber);

  const onChallengeCodeChange = useCallback(
    (e) => {
      setChallengeCode(e.target.value);
      setErrorMessage();
    },
    [setChallengeCode, setErrorMessage],
  );

  const onRequestChallenge = useCallback(() => {
    setHasRequestedChallenge(true);

    return requestChallenge(challengeCode)
      .then(({ data = {} }) => {
        const { isVerified } = data;
        const rejectionReason = data.rejectionReason || unknown;

        if (isVerified) {
          // setting phone verification back to null in order to update providerIdRequiringPhoneVerification in context. A null value will get rid of the phone verification banner.
          setVerification(null);
          trackEvent(STAMPS_SMS_VERIFY_CODE_SUCCESS);
          return goToNextStep({
            isVerified,
          });
        }

        const okButVerificationFailed = okMessagesWithFailedVerification.some((message) => rejectionReason.includes(message));
        if (okButVerificationFailed) {
          trackEvent(STAMPS_SMS_VERIFY_CODE_FAILURE_CONTACT_STAMPS, {
            rejectionReason,
          });
          return goToNextStep({
            isVerified: false,
          });
        }

        if (rejectionReason === invalidCodeResponse) {
          trackEvent(STAMPS_SMS_VERIFY_CODE_WRONG_CODE);
        }

        setHasRequestedChallenge(false);
        setErrorMessage(rejectionReason);
      })
      .catch((e) => {
        trackEvent(STAMPS_SMS_VERIFY_CODE_FAILURE_CONTACT_STAMPS, e);
        return goToNextStep({
          isVerified: false,
        });
      });
  }, [challengeCode, goToNextStep, setVerification, requestChallenge, trackEvent]);

  const onRequestChallengeCode = useCallback(() => {
    setHasRequestedCode(true);

    const clearFeedbackAfterOneSecond = () =>
      setTimeout(() => {
        setHasRequestedCode(false);
        setRequestCodeSuccess(undefined);
      }, ONE_SECOND);

    trackEvent(STAMPS_SMS_RESEND_CODE);

    return requestChallengeCode(phoneNumber)
      .then(() => {
        setRequestCodeSuccess(true);
      })
      .catch(() => {
        setRequestCodeSuccess(false);
      })
      .finally(() => {
        clearFeedbackAfterOneSecond();
      });
  }, [phoneNumber, requestChallengeCode, trackEvent]);

  return (
    <>
      <ModalBody className={classnames(style.wrapper, className)}>
        <div className={sharedStyle['title-text']}>
          <FormattedMessage defaultMessage='We Sent You A Code' description='phoneVerifyModal.challengeTitle' id='+QRJsf' />
        </div>
        <div className={sharedStyle['subtitle-text']}>
          <FormattedMessage
            defaultMessage='Code has been sent to phone number: {formattedNumber}'
            description='phoneVerifyModal.challengeSubtitle'
            id='b/I2hH'
            values={{ formattedNumber }}
          />
        </div>
        <div className={style['code-field']}>
          <label htmlFor='code-input'>
            <FormattedMessage defaultMessage='ENTER CODE' description='phoneVerifyModal.enterCode' id='3Q4iHw' />
          </label>
          <input id='code-input' onChange={onChallengeCodeChange} value={challengeCode} />
        </div>
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        <ResendLink disabled={hasRequestedCode} onClick={onRequestChallengeCode} wasResendSuccessful={requestCodeSuccess} />
      </ModalBody>
      <ModalFooter className={sharedStyle.footer}>
        <Button onClick={goToPreviousStep} theme='tertiary'>
          <FormattedMessage defaultMessage='Back' description='phoneVerifyModal.backButton' id='iDVQgd' />
        </Button>
        <Button disabled={isNextDisabled} onClick={onRequestChallenge} theme='primary'>
          <FormattedMessage defaultMessage='Submit' description='phoneVerifyModal.submitButton' id='Uh64+T' />
        </Button>
      </ModalFooter>
    </>
  );
};

VerificationChallenge.propTypes = {
  className: PropTypes.string,
  phoneNumber: PropTypes.string,
  goToPreviousStep: PropTypes.func.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  requestChallengeCode: PropTypes.func.isRequired,
  requestChallenge: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  setVerification: PropTypes.func,
};
